import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi, approvalsApi, purchaseRequestsApi, dashboardApi } from '../lib/api';
import { PR_STATUS_LABELS } from '../types';
import type { CurrentUser, PurchaseRequest, SavingsDashboard } from '../types';
import AppShell from '../components/AppShell';
import { useTenantContext } from '../context/TenantContext';

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  surface:  '#ffffff',
  surface2: '#f1f5f9',
  canvas:   '#f8fafc',
  primary:  '#0ea5e9',
  green:    '#10b981',
  amber:    '#f59e0b',
  red:      '#ef4444',
  textPrimary:   '#0f172a',
  textSecondary: '#475569',
  textMuted:     '#94a3b8',
  border:   '#e2e8f0',
} as const;

const SANS = "'Roboto', system-ui, sans-serif";
const MONO = "'Courier New', monospace";

// ── Badge styles — light theme ─────────────────────────────────────────────────
type BadgeVariant = 'green' | 'amber' | 'red' | 'teal' | 'slate';

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft:            'slate',
  submitted:        'teal',
  pending_approval: 'amber',
  approved:         'green',
  rejected:         'red',
  cancelled:        'slate',
  ordered:          'teal',
};

const BADGE_STYLE: Record<BadgeVariant, React.CSSProperties> = {
  green: { background: '#d1fae5', color: '#065f46' },
  amber: { background: '#fef3c7', color: '#92400e' },
  red:   { background: '#fee2e2', color: '#991b1b' },
  teal:  { background: '#e0f2fe', color: '#0369a1' },
  slate: { background: '#f1f5f9', color: '#475569' },
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();
  const { selectedTenantId } = useTenantContext();
  const [user, setUser]         = useState<CurrentUser | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [recentPrs, setRecentPrs]   = useState<PurchaseRequest[]>([]);
  const [savings, setSavings]       = useState<SavingsDashboard | null>(null);

  useEffect(() => {
    authApi.me()
      .then((u) => {
        setUser(u);
        purchaseRequestsApi.list().then((prs) => setRecentPrs(prs.slice(0, 5))).catch(() => {});
        approvalsApi.inbox().then((s) => setPendingCount(s.length)).catch(() => {});
      })
      .catch(() => navigate('/login', { replace: true }));
  }, [navigate]);

  const isSuperAdmin = user?.role === 'super_admin';
  useEffect(() => {
    if (!user) return;
    if (isSuperAdmin && selectedTenantId === undefined) return;
    dashboardApi.savings(isSuperAdmin ? selectedTenantId : undefined).then(setSavings).catch(() => {});
  }, [user, isSuperAdmin, selectedTenantId]);

  if (!user) return null;

  const canApprove = ['cfo', 'procurement_head', 'procurement_manager', 'procurement_executive'].includes(user.role);
  const now        = new Date();
  const quarter    = `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`;

  const totalSpend  = savings?.totalAssessedAcv ?? 0;
  const savingsLow  = savings?.latestRun?.savingsLow ?? null;
  const savingsHigh = savings?.latestRun?.savingsHigh ?? null;
  const renewals90  = savings?.upcomingRenewals?.filter((r) => r.daysUntilRenewal <= 90) ?? [];
  const nextRenewal = renewals90[0];

  const topbarActions = (
    <>
      <button className="sl-btn-ghost" style={{
        padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 400,
        cursor: 'pointer', border: `1px solid ${C.border}`, fontFamily: SANS,
        background: 'transparent', color: C.textSecondary, transition: 'all 0.15s',
      }}>⬇ Export</button>
      <Link to="/purchase-requests/new" style={{ textDecoration: 'none' }}>
        <button className="sl-btn-primary" style={{
          padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 400,
          cursor: 'pointer', border: 'none', fontFamily: SANS,
          background: C.primary, color: '#ffffff', transition: 'all 0.15s',
          boxShadow: '0 1px 3px rgba(14,165,233,0.3)',
        }}>+ New Request</button>
      </Link>
    </>
  );

  return (
    <AppShell user={user} title="Dashboard" subtitle={`SpendLens · ${quarter}`} actions={topbarActions}>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard
          label="Total Assessed Spend"
          value={totalSpend > 0 ? formatAcv(totalSpend) : '—'}
          sub="Annual contract value"
          accent={C.primary}
          to="/savings"
        />
        <StatCard
          label="Identified Savings"
          value={savingsLow != null && savingsHigh != null
            ? `${formatAcv(savingsLow)} – ${formatAcv(savingsHigh)}`
            : '—'}
          sub={savings?.latestRun
            ? `Low–high range · ${savings.latestRun.vendorCount} vendors`
            : 'Run benchmarks to see savings'}
          accent={C.green}
          to="/savings"
        />
        <StatCard
          label="Pending Approvals"
          value={canApprove ? pendingCount : '—'}
          sub={canApprove ? 'Awaiting your decision' : 'Not applicable for your role'}
          accent={C.amber}
          to="/approvals/inbox"
        />
        <StatCard
          label="Renewals in 90 Days"
          value={renewals90.length > 0 ? renewals90.length : '0'}
          sub={nextRenewal
            ? `${nextRenewal.name} · ${nextRenewal.annualContractValue ? formatAcv(nextRenewal.annualContractValue) + ' at stake' : `${nextRenewal.daysUntilRenewal}d away`}`
            : 'No upcoming renewals'}
          accent={renewals90.length > 0 ? C.red : C.textMuted}
          to="/vendors"
        />
      </div>

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <QuickCard icon="◫" title="New Purchase Request"
          desc="Submit a vendor spend request for approval"
          cta="Raise Request →" to="/purchase-requests/new" />
        <QuickCard icon="◈" title="Vendor Registry"
          desc="Contracts, renewal dates, benchmark status"
          cta="View Registry →" to="/vendors" />
        <QuickCard icon="◉" title="Savings Dashboard"
          desc="Identified savings and top renegotiation targets"
          cta="View Savings →" to="/savings" />
      </div>

      {/* Recent requests */}
      {recentPrs.length > 0 && (
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
          overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <div style={{
            padding: '16px 20px', borderBottom: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, letterSpacing: '-0.1px' }}>Recent Requests</div>
            <Link to="/purchase-requests" style={{ fontSize: 11.5, color: C.primary, textDecoration: 'none', fontWeight: 400 }}>
              View all →
            </Link>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.canvas }}>
                {['#', 'Title / Vendor', 'Amount', 'Status', 'Awaiting', 'Submitted'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentPrs.map((pr) => <PrRow key={pr.id} pr={pr} />)}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent, to }: {
  label: string; value: number | string; sub: string; accent: string; to: string;
}) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div className="sl-stat-card" style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderTop: `3px solid ${accent}`,
        borderRadius: 10,
        padding: '18px 20px',
        transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
        height: '100%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontSize: 10.5, fontWeight: 500, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
          {label}
        </div>
        <div style={{ fontSize: 28, fontWeight: 300, letterSpacing: '-0.5px', lineHeight: 1, marginBottom: 6, color: '#0f172a' }}>
          {value}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>
      </div>
    </Link>
  );
}

function QuickCard({ icon, title, desc, cta, to }: {
  icon: string; title: string; desc: string; cta: string; to: string;
}) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div className="sl-quick-card" style={{
        background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '16px 18px', cursor: 'pointer', transition: 'all 0.2s', height: '100%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontSize: 20, marginBottom: 10 }}>{icon}</div>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#0f172a' }}>{title}</div>
        <div style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.4, marginBottom: 10 }}>{desc}</div>
        <div style={{ fontSize: 12, color: '#0ea5e9', fontWeight: 400 }}>{cta}</div>
      </div>
    </Link>
  );
}

function PrRow({ pr }: { pr: PurchaseRequest }) {
  const navigate   = useNavigate();
  const variant    = STATUS_BADGE[pr.status] ?? 'slate';
  const vendorName = pr.vendor?.name ?? (pr.metadata?.vendorName as string | undefined) ?? '—';
  const prId       = `#${pr.id.slice(-6).toUpperCase()}`;
  const awaiting   = pr.currentApprovalStep?.approver?.name ?? '—';
  const submitted  = pr.submittedAt ? timeSince(pr.submittedAt) : timeSince(pr.createdAt);

  return (
    <tr className="sl-tr" style={{ cursor: 'pointer' }} onClick={() => navigate(`/purchase-requests/${pr.id}`)}>
      <td style={tdStyle}>
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: C.textMuted }}>{prId}</span>
      </td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: C.surface2, border: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 500, color: C.textMuted,
          }}>
            {vendorName !== '—' ? vendorName[0].toUpperCase() : '◫'}
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 400, color: C.textPrimary }}>{pr.title}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1 }}>
              {vendorName} · {pr.type.replace(/_/g, ' ')}
            </div>
          </div>
        </div>
      </td>
      <td style={tdStyle}>
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: C.textPrimary }}>{formatCurrency(pr.amount, pr.currency)}</span>
      </td>
      <td style={tdStyle}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
          borderRadius: 20, fontSize: 11, fontWeight: 400, whiteSpace: 'nowrap',
          ...BADGE_STYLE[variant],
        }}>
          {PR_STATUS_LABELS[pr.status]}
        </span>
      </td>
      <td style={{ ...tdStyle, color: C.textMuted, fontSize: 12 }}>{awaiting}</td>
      <td style={{ ...tdStyle, color: C.textMuted, fontSize: 12 }}>{submitted}</td>
    </tr>
  );
}

// ── Shared style constants ─────────────────────────────────────────────────────
const thStyle: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 500, color: '#94a3b8', letterSpacing: '0.06em',
  textTransform: 'uppercase', padding: '10px 20px', textAlign: 'left',
  borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 20px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'middle',
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function formatCurrency(amount: string, currency: string) {
  return parseFloat(amount).toLocaleString('en-US', { style: 'currency', currency });
}

function formatAcv(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function timeSince(dateStr: string): string {
  const secs = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (secs < 60)    return 'just now';
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
